import React, { useState, useRef, useEffect } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { Button } from '@/components/ui/button';
import { Card, CardContent } from '@/components/ui/card';
import PageIndicator from '@/components/PageIndicator';
import SignatureCanvas from '@/components/SignatureCanvas';
import { generateAndSavePagePDF } from '@/utils/pdfGenerator';
import html2pdf from 'html2pdf.js';

// Local image paths to avoid CORS issues with html2canvas PDF generation
const LOGO_URL = '/images/TCA_Logo.png';
const BANNER_URL = '/images/TCA_Banner.png';
const TCA_SHORT_LOGO = '/images/TCA_Short_Logo.png';

// The drawn signature cropped to its own strokes (plus a small margin), as a PNG data URL -
// null when the pad is empty. Same as on the POA site (poa.tcauditors.co.za).
const cropSignature = (canvas) => {
  const { width, height } = canvas;
  if (!width || !height) return null;
  const pixels = canvas.getContext('2d').getImageData(0, 0, width, height).data;
  let minX = width, minY = height, maxX = -1, maxY = -1;
  for (let y = 0; y < height; y++) {
    for (let x = 0; x < width; x++) {
      if (pixels[(y * width + x) * 4 + 3] > 20) {
        if (x < minX) minX = x;
        if (x > maxX) maxX = x;
        if (y < minY) minY = y;
        if (y > maxY) maxY = y;
      }
    }
  }
  if (maxX < 0) return null;
  const pad = 4;
  const x0 = Math.max(0, minX - pad), y0 = Math.max(0, minY - pad);
  const w = Math.min(width, maxX + pad + 1) - x0, h = Math.min(height, maxY + pad + 1) - y0;
  const out = document.createElement('canvas');
  out.width = w;
  out.height = h;
  out.getContext('2d').drawImage(canvas, x0, y0, w, h, 0, 0, w, h);
  return out.toDataURL('image/png');
};

const PageTwo = () => {
  const location = useLocation();
  const navigate = useNavigate();
  const clientData = location.state || {};
  
  // Get formId from previous page
  const formId = clientData.formId;
  const [isGeneratingPDF, setIsGeneratingPDF] = useState(false);
  
  // Ref for signature canvas to restore signature before PDF generation
  const signatureCanvasRef = useRef(null);
  
  // Initialize with previous data if coming back from Page 3
  const [signature, setSignature] = useState(clientData.signaturePage2 || null);
  const [signedAt, setSignedAt] = useState(clientData.signedAtPage2 || '');
  const [locationError, setLocationError] = useState('');

  // Scroll to top on component mount
  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  // Get today's date formatted
  const today = new Date();
  const dayOfMonth = today.getDate();
  const monthYear = today.toLocaleDateString('en-ZA', { month: 'long', year: 'numeric' });
  const fullDate = today.toLocaleDateString('en-ZA', { day: 'numeric', month: 'long', year: 'numeric' });

  // Construct full name from client data
  const getFullName = () => {
    const parts = [
      clientData.firstName,
      clientData.secondName,
      clientData.middleName,
      clientData.surname
    ].filter(Boolean);
    return parts.join(' ') || '[Client Full Name]';
  };

  const fullName = getFullName();
  const idNumber = clientData.idNumber || '[ID Number]';
  const taxNumber = clientData.taxNumber || '[Tax Number]';

  // Validate location - only allow alphabetic letters and spaces
  const validateLocation = (value) => {
    if (!value || value.trim() === '') {
      return '';
    }
    // Check if the value contains only letters and spaces
    const locationRegex = /^[a-zA-Z\s]+$/;
    if (!locationRegex.test(value)) {
      return 'Location can only contain letters (no numbers or special characters)';
    }
    return '';
  };

  const handleLocationChange = (e) => {
    const value = e.target.value;
    setSignedAt(value);
    setLocationError(validateLocation(value));
  };

  const handleSignatureChange = (signatureData) => {
    setSignature(signatureData);
  };

  const handleClearSignature = () => {
    setSignature(null);
  };

  // Check if form is valid (location and signature required)
  const isFormValid = () => {
    return signedAt && signedAt.trim() !== '' && signature && !locationError;
  };

  const handleSubmit = async () => {
    if (!signedAt || signedAt.trim() === '') {
      alert('Please enter the location where you are signing.');
      return;
    }
    // Validate location before proceeding
    const locationValidationError = validateLocation(signedAt);
    if (locationValidationError) {
      alert(locationValidationError);
      return;
    }
    if (!signature) {
      alert('Please provide your signature before proceeding.');
      return;
    }
    
    // Restore signature to canvas before PDF generation (in case it was cleared by zoom/resize)
    if (signatureCanvasRef.current) {
      signatureCanvasRef.current.restoreSignature();
    }
    
    // Generate PDF of current page before navigating
    setIsGeneratingPDF(true);
    
    // Create full-screen overlay to hide PDF generation process from user
    const overlay = document.createElement('div');
    overlay.id = 'pdf-generation-overlay';
    overlay.style.cssText = `
      position: fixed;
      top: 0;
      left: 0;
      width: 100vw;
      height: 100vh;
      background: rgba(255, 255, 255, 0.97);
      display: flex;
      flex-direction: column;
      align-items: center;
      justify-content: center;
      z-index: 99999;
    `;
    overlay.innerHTML = `
      <div style="text-align: center;">
        <div style="width: 48px; height: 48px; border: 4px solid #e5e7eb; border-top-color: #f97316; border-radius: 50%; animation: spin 1s linear infinite; margin: 0 auto 16px;"></div>
        <p style="font-size: 18px; font-weight: 600; color: #1f2937; margin: 0;">Saving your progress...</p>
        <p style="font-size: 14px; color: #6b7280; margin-top: 8px;">Please wait while we generate your PDF</p>
      </div>
      <style>@keyframes spin { to { transform: rotate(360deg); } }</style>
    `;
    document.body.appendChild(overlay);
    
    try {
      const element = document.querySelector('#page-pdf-content');
      if (!element) {
        console.error('[Page2] Element #page-pdf-content not found!');
        setIsGeneratingPDF(false);
        overlay.remove();
        return;
      }
      
      // Store original values for restoration
      const originalStyles = new Map();
      const hiddenElements = [];
      const originalClassNames = new Map();
      
      const hideElement = (el) => {
        if (el) {
          originalStyles.set(el, el.style.cssText);
          el.style.display = 'none';
          hiddenElements.push(el);
        }
      };
      
      const modifyStyle = (el, newStyle) => {
        if (el) {
          if (!originalStyles.has(el)) {
            originalStyles.set(el, el.style.cssText);
          }
          Object.assign(el.style, newStyle);
        }
      };
      
      const modifyClassName = (el, newClassName) => {
        if (el) {
          if (!originalClassNames.has(el)) {
            originalClassNames.set(el, el.className);
          }
          el.className = newClassName;
        }
      };
      
      // === PDF MODIFICATIONS ===
      // Keep the PDF's original top spacing (the page itself has only 2px above the logo; was py-6 sm:py-8)
      modifyStyle(element, { paddingTop: window.matchMedia('(min-width: 640px)').matches ? '2rem' : '1.5rem' });
      
      // 1. Hide company logo, banner, and "TAX SERVICES AGREEMENT" heading
      element.querySelectorAll('.mb-4.sm\\:mb-6').forEach(el => {
        if (el.querySelector('img[alt="TCA Auditors Logo"]')) {
          hideElement(el);
        }
      });
      element.querySelectorAll('h1, h2').forEach(el => {
        if (el.textContent?.includes('TAX SERVICES AGREEMENT')) {
          hideElement(el.closest('.mb-6') || el);
        }
      });
      element.querySelectorAll('img[alt="TCA Auditors Logo"], img[alt*="Tax & Auditing"]').forEach(img => {
        const container = img.closest('.mb-4, .mb-6, .flex.flex-col');
        if (container) {
          hideElement(container);
        }
      });
      
      // 2. Reduce SARS logo size by 5%
      element.querySelectorAll('img[alt="SARS Logo"]').forEach(img => {
        const currentHeight = img.offsetHeight;
        modifyStyle(img, { height: (currentHeight * 0.95) + 'px', width: 'auto' });
      });
      
      // 3. Reduce "SOUTH AFRICAN REVENUE SERVICE" text by 5px total
      element.querySelectorAll('p, .text-xl, .text-2xl').forEach(el => {
        if (el.textContent?.includes('SOUTH AFRICAN REVENUE SERVICE')) {
          const computedStyle = window.getComputedStyle(el);
          const currentSize = parseFloat(computedStyle.fontSize);
          modifyStyle(el, { fontSize: (currentSize - 5) + 'px' });
        }
      });
      
      // 3b. Reduce "Special Power of Attorney to Tax Practitioner" text by 2px
      element.querySelectorAll('p, .text-lg').forEach(el => {
        if (el.textContent?.includes('Special Power of Attorney to Tax Practitioner')) {
          const computedStyle = window.getComputedStyle(el);
          const currentSize = parseFloat(computedStyle.fontSize);
          modifyStyle(el, { fontSize: (currentSize - 2) + 'px' });
        }
      });
      
      // 3c. Increase "The Commissioner for the South African Revenue Service" by 1px
      // Also add 4px margin below to create space before "I, the undersigned,"
      element.querySelectorAll('p, strong, .font-semibold').forEach(el => {
        if (el.textContent?.includes('The Commissioner for the South African Revenue Service')) {
          const computedStyle = window.getComputedStyle(el);
          const currentSize = parseFloat(computedStyle.fontSize);
          modifyStyle(el, { fontSize: (currentSize + 1) + 'px', marginBottom: '4px' });
        }
      });
      
      // 3c2. Add spacing above "I, the undersigned," paragraph to prevent overlap with heading
      element.querySelectorAll('p').forEach(el => {
        if (el.textContent?.trim().startsWith('I, the undersigned,')) {
          modifyStyle(el, { marginTop: '10px', paddingTop: '6px' });
        }
      });
      
      // 3d. Reduce font size for all text below "The Commissioner..." by 3px
      // Target all paragraphs, spans, and text elements in the main content area
      element.querySelectorAll('.space-y-4 p, .space-y-6 p, .mt-6 p, .mb-4 p, .mb-6 p').forEach(el => {
        // Skip the header texts we already modified
        if (el.textContent?.includes('SOUTH AFRICAN REVENUE SERVICE')) return;
        if (el.textContent?.includes('Special Power of Attorney to Tax Practitioner')) return;
        if (el.textContent?.includes('The Commissioner for the South African Revenue Service')) return;
        
        const computedStyle = window.getComputedStyle(el);
        const currentSize = parseFloat(computedStyle.fontSize);
        if (currentSize > 10) { // Only reduce if larger than 10px to avoid making text too small
          modifyStyle(el, { fontSize: (currentSize - 3) + 'px' });
        }
      });
      
      // 3e. Reduce bullet point text below "1. INCOME TAX REFERENCE" by 2px extra (total 5px)
      element.querySelectorAll('li, ul li, ol li').forEach(el => {
        const computedStyle = window.getComputedStyle(el);
        const currentSize = parseFloat(computedStyle.fontSize);
        if (currentSize > 10) {
          modifyStyle(el, { fontSize: (currentSize - 5) + 'px' });
        }
      });
      
      // Also reduce font size on labels and other text elements by 3px
      element.querySelectorAll('label, span:not(.bg-blue-50):not([class*="bg-blue"])').forEach(el => {
        const computedStyle = window.getComputedStyle(el);
        const currentSize = parseFloat(computedStyle.fontSize);
        if (currentSize > 10) {
          modifyStyle(el, { fontSize: (currentSize - 3) + 'px' });
        }
      });
      
      // 3f. Add spacing before "Signed at" block to prevent text overlap with "24 months" sentence
      element.querySelectorAll('p, div').forEach(el => {
        if (el.textContent?.includes('Signed at') && el.textContent?.includes('on this')) {
          modifyStyle(el, { marginTop: '8px', paddingTop: '4px' });
        }
      });
      
      // 4. Reduce top margin/padding
      modifyStyle(element, { paddingTop: '2px' });
      // The blank strip below the frame (pb-6/sm:pb-8) is not needed in the PDF; with the page
      // full it could spill onto an empty 2nd page (since the grey-box gaps, 2026-10-27)
      modifyStyle(element, { paddingBottom: '2px' });
      // ...and the empty strip inside the frame below the footer (p-8; the buttons there are
      // hidden in the PDF) - with a long name on 2 lines the frame's bottom edge spilled over
      element.querySelectorAll('.sm\\:p-8').forEach(el => {
        modifyStyle(el, { paddingBottom: '12px' });
      });
      element.querySelectorAll('.py-6, .sm\\:py-8, .py-4').forEach(el => {
        modifyStyle(el, { paddingTop: '2px', paddingBottom: '2px' });
      });
      element.querySelectorAll('.flex.justify-between.items-start.mb-6').forEach(el => {
        modifyStyle(el, { marginTop: '0px', paddingTop: '0px', marginBottom: '4px' });
      });
      
      // 5. Signature canvas - LEFT ALIGNED and sized appropriately
      element.querySelectorAll('.border-dashed, [class*="border-dashed"]').forEach(el => {
        if (el.querySelector('canvas') || el.textContent?.includes('Draw your signature')) {
          modifyStyle(el, { 
            width: '60%', maxWidth: '420px', height: '84px', minHeight: '84px', maxHeight: '84px',
            margin: '0', overflow: 'hidden'
          });
        }
      });
      element.querySelectorAll('canvas').forEach(canvas => {
        modifyStyle(canvas, { width: '100%', height: '72px', maxHeight: '72px' });
      });
      element.querySelectorAll('.h-40, .h-48, .sm\\:h-48').forEach(el => {
        if (el.closest('[class*="signature"]') || el.querySelector('canvas') || el.tagName === 'CANVAS') {
          modifyStyle(el, { height: '72px', minHeight: '72px', maxHeight: '72px' });
        }
      });
      element.querySelectorAll('.space-y-3').forEach(el => {
        if (el.querySelector('canvas')) {
          modifyStyle(el, { width: '60%', maxWidth: '420px', margin: '0' });
        }
      });
      element.querySelectorAll('.mt-6').forEach(el => {
        const hasSignatureLabel = el.querySelector('p')?.textContent?.includes('Signature:');
        if (hasSignatureLabel) {
          const canvasContainer = el.querySelector('.space-y-3');
          if (canvasContainer) {
            modifyStyle(canvasContainer, { width: '60%', maxWidth: '420px', margin: '0', marginTop: '10px' });
          }
          // Add 10px gap between "Signature:" label and the canvas
          const signatureLabel = el.querySelector('p');
          if (signatureLabel && signatureLabel.textContent?.includes('Signature:')) {
            modifyStyle(signatureLabel, { marginBottom: '10px', paddingBottom: '5px' });
          }
        }
      });

      // 5a. The client's signature fills its box (owner, 2026-10-27): the pad is much wider and
      // taller than the box, so the whole pad shrunk into it left the signature small. Instead
      // the strokes, cropped to their own edges, are shown at full box size (proportions kept).
      const sigCanvas = element.querySelector('canvas');
      const sigCrop = sigCanvas && cropSignature(sigCanvas);
      if (sigCrop) {
        const sigImg = document.createElement('img');
        sigImg.id = 'temp-signature-image';
        sigImg.alt = '';
        sigImg.src = sigCrop;
        sigImg.style.cssText = 'display:block;width:100%;height:72px;object-fit:contain;object-position:center;';
        sigCanvas.parentNode.insertBefore(sigImg, sigCanvas);
        hideElement(sigCanvas);
        await sigImg.decode().catch(() => {});
      }

      // 5b. Gap between signature section and "As witnesses" section
      element.querySelectorAll('.mt-6').forEach(el => {
        if (el.querySelector('canvas') || el.textContent?.includes('Signature:')) {
          modifyStyle(el, { marginBottom: '12px' });
        }
      });
      element.querySelectorAll('p').forEach(p => {
        if (p.textContent?.trim() === 'As witnesses:') {
          const parentDiv = p.closest('.mt-6');
          if (parentDiv) {
            modifyStyle(parentDiv, { marginTop: '16px', paddingTop: '0px' });
          }
          // Add spacing between "As witnesses:" and the 1st witness - use padding to avoid override
          modifyStyle(p, { marginBottom: '24px', paddingBottom: '16px' });
        }
      });
      
      // Compact layout spacing - aggressive reduction for single page
      element.querySelectorAll('.space-y-4 > *:not(:first-child)').forEach(el => {
        modifyStyle(el, { marginTop: '3px' });
      });
      // ...but the two grey boxes (tax matters, "Signed at") need room above them: at 3px they
      // painted over the bottom of the line before them ("...detailed below:", "...24 month(s)
      // from the date of my signature.") - 2026-10-27, as on the POA site; this also restores
      // step 3f's gap.
      element.querySelectorAll('.space-y-4 > .bg-gray-50').forEach(el => {
        modifyStyle(el, { marginTop: '10px' });
      });
      element.querySelectorAll('.space-y-6 > *:not(:first-child)').forEach(el => {
        modifyStyle(el, { marginTop: '4px' });
      });
      element.querySelectorAll('.space-y-8 > *:not(:first-child)').forEach(el => {
        modifyStyle(el, { marginTop: '5px' });
      });
      element.querySelectorAll('.mb-4').forEach(el => {
        modifyStyle(el, { marginBottom: '4px' });
      });
      element.querySelectorAll('.mb-6').forEach(el => {
        modifyStyle(el, { marginBottom: '6px' });
      });
      element.querySelectorAll('.mb-8').forEach(el => {
        modifyStyle(el, { marginBottom: '8px' });
      });
      element.querySelectorAll('p').forEach(el => {
        modifyStyle(el, { marginBottom: '1px', lineHeight: '1.3', display: 'block', whiteSpace: 'normal' });
      });
      // A long name pushes the ID number onto a 2nd line, and at 1.3 its blue box painted over
      // the bottom of "I, the undersigned," above it (2026-10-27) - room for the boxes
      element.querySelectorAll('p').forEach(el => {
        if (el.textContent?.trim().startsWith('I, the undersigned,')) {
          modifyStyle(el, { lineHeight: '2' });
        }
      });
      element.querySelectorAll('h2, h3, h4').forEach(el => {
        modifyStyle(el, { marginBottom: '2px', marginTop: '4px' });
      });
      element.querySelectorAll('label').forEach(el => {
        modifyStyle(el, { marginBottom: '1px' });
      });
      
      // Fix blue fields - text at baseline, background extended down to cover text
      element.querySelectorAll('.bg-blue-50, [class*="bg-blue"]').forEach(field => {
        if (field.tagName === 'INPUT') return;
        if (field.classList.contains('border-gray-400')) return;
        
        const newClassName = field.className.replace(/border-b-2/g, '').replace(/border-tca-blue/g, '');
        modifyClassName(field, newClassName);
        
        modifyStyle(field, { 
          display: 'inline-block', position: 'relative', top: '-2px',
          padding: '0px 6px 5px 6px', margin: '0 2px', borderRadius: '3px',
          lineHeight: '1.4', verticalAlign: 'baseline', boxSizing: 'border-box',
          border: 'none', borderBottom: 'none', borderStyle: 'none',
          textDecoration: 'none', backgroundColor: '#dbeafe'
        });
      });
      
      // Only target spans with border-tca-blue
      element.querySelectorAll('.border-tca-blue').forEach(el => {
        if (el.classList.contains('bg-blue-50')) {
          const newClassName = el.className.replace(/border-b-2/g, '').replace(/border-tca-blue/g, '');
          modifyClassName(el, newClassName);
          modifyStyle(el, { border: 'none', borderBottom: 'none', borderStyle: 'none' });
        }
      });
      
      // Replace input fields with span - text at baseline, background extended down
      element.querySelectorAll('input.bg-blue-50, input[class*="bg-blue"]').forEach(input => {
        const inputValue = input.value || input.placeholder || '';
        const replacementSpan = document.createElement('span');
        replacementSpan.textContent = inputValue;
        replacementSpan.style.cssText = `
          display: inline-block; position: relative; top: -2px;
          padding: 0px 6px 5px 6px; background-color: #dbeafe; color: #1e3a8a;
          font-weight: bold; border-radius: 3px; min-width: 80px;
          line-height: 1.4; vertical-align: baseline;
        `;
        replacementSpan.id = 'temp-input-replacement-' + Math.random().toString(36).substr(2, 9);
        input.style.display = 'none';
        input.parentNode.insertBefore(replacementSpan, input.nextSibling);
      });
      
      // Reduce grid gaps
      element.querySelectorAll('.grid.grid-cols-1.sm\\:grid-cols-2').forEach(el => {
        modifyStyle(el, { gap: '4px', marginTop: '2px', marginBottom: '2px' });
      });
      element.querySelectorAll('.border-t').forEach(el => {
        modifyStyle(el, { marginTop: '4px', paddingTop: '4px' });
      });
      
      // 6. Reduce gaps between witness sections
      element.querySelectorAll('br').forEach(br => {
        modifyStyle(br, { display: 'none' });
      });
      
      // 6b. Fix witness signature lines
      element.querySelectorAll('.w-52.border-b.border-gray-400').forEach(line => {
        modifyStyle(line, {
          display: 'inline-block', width: '150px', height: '1px',
          borderBottom: '1px solid #9ca3af', marginRight: '8px',
          verticalAlign: 'middle', transform: 'translateY(6px)',
          marginTop: '0px', marginBottom: '0px'
        });
      });
      
      // 7. Set gap between witness 1 and witness 2
      element.querySelectorAll('.mt-6').forEach(el => {
        if (el.textContent?.includes('witnesses')) {
          el.querySelectorAll('.flex.items-center.gap-2').forEach((witness, index) => {
            if (index === 0) {
              modifyStyle(witness, { marginTop: '10px', marginBottom: '48px' });
            } else {
              modifyStyle(witness, { marginTop: '0px', marginBottom: '10px' });
            }
          });
        }
      });
      
      // 8. Reduce gap between witness 2 and Version section
      element.querySelectorAll('.mt-8.pt-4.border-t').forEach(el => {
        modifyStyle(el, { marginTop: '8px', paddingTop: '4px' });
      });
      
      // Hide elements marked for PDF exclusion
      element.querySelectorAll('[data-hide-in-pdf="true"]').forEach(el => hideElement(el));
      
      // Remove all footers - not needed on this page and creates space at bottom
      // No footer added for Page 2
      
      // Generate PDF - Single page, natural content flow
      const pdfOptions = {
        margin: [3, 5, 3, 5],
        filename: `Page02_${formId || 'form'}.pdf`,
        image: { type: 'jpeg', quality: 0.95 },
        html2canvas: { 
          scale: 2, useCORS: true, logging: false, letterRendering: true,
          scrollX: 0, scrollY: 0, windowWidth: element.scrollWidth
        },
        jsPDF: { unit: 'mm', format: 'a4', orientation: 'portrait' }
      };
      
      // Where the witness lines land on the A4 page, for the server to sign on them (owner,
      // 2026-10-27: Ruan's and Anel's signatures are added by the server, never shown here).
      // html2pdf lays a copy of the page out in its own container, exactly as wide as the PDF's
      // inner width (210mm - 2 x 5mm margins), and photographs that container - so the lines are
      // measured in that copy (the page on screen is wider and wraps differently).
      let witnessLines = [];
      let signatureHeightMm = 0;
      const pdfWorker = html2pdf().set(pdfOptions).from(element).toContainer();
      const pdfBlob = await pdfWorker.then(() => {
        const container = pdfWorker.prop.container;
        const box = container.getBoundingClientRect();
        const mmPerPx = (210 - pdfOptions.margin[1] - pdfOptions.margin[3]) / box.width;
        const toMm = (v) => Math.round(v * mmPerPx * 100) / 100;
        witnessLines = [...container.querySelectorAll('[data-witness-line]')].map((line) => {
          const r = line.getBoundingClientRect();
          return { x: pdfOptions.margin[1] + toMm(r.left - box.left), y: pdfOptions.margin[0] + toMm(r.bottom - box.top), w: toMm(r.width) };
        });
        const sigBox = container.querySelector('#temp-signature-image') || container.querySelector('canvas');
        signatureHeightMm = sigBox ? toMm(sigBox.getBoundingClientRect().height) : 0;
      }).outputPdf('blob');

      // === RESTORE ORIGINAL STATE ===
      const tempFooter = document.getElementById('temp-pdf-footer');
      if (tempFooter) tempFooter.remove();
      const tempSignature = document.getElementById('temp-signature-image');
      if (tempSignature) tempSignature.remove();
      element.querySelectorAll('[id^="temp-input-replacement-"]').forEach(span => span.remove());
      element.querySelectorAll('input').forEach(input => { 
        if (input.style.display === 'none') input.style.display = ''; 
      });
      originalClassNames.forEach((origClassName, el) => { el.className = origClassName; });
      originalStyles.forEach((origStyle, el) => { el.style.cssText = origStyle; });
      
      // Save to backend with retry logic and verification
      if (formId) {
        const base64Data = await new Promise((resolve, reject) => {
          const reader = new FileReader();
          reader.onloadend = () => resolve(reader.result.split(',')[1]);
          reader.onerror = () => reject(reader.error);
          reader.readAsDataURL(pdfBlob);
        });
        
        const MAX_RETRIES = 3;
        let saveSuccessful = false;
        const API_URL = process.env.REACT_APP_BACKEND_URL || '';
        
        for (let attempt = 1; attempt <= MAX_RETRIES; attempt++) {
          try {
            console.log(`[Page2] Save attempt ${attempt}/${MAX_RETRIES}...`);
            
            const response = await fetch(`${API_URL}/api/pdf/save-page`, {
              method: 'POST',
              headers: { 'Content-Type': 'application/json' },
              body: JSON.stringify({ 
                form_id: formId, 
                page_number: 2, 
                pdf_data: base64Data,
                page_title: 'Terms and Conditions Acceptance',
                client_data: clientData,
                witness_lines: witnessLines,
                signature_height_mm: signatureHeightMm
              })
            });
            
            if (!response.ok) {
              throw new Error(`Server returned ${response.status}`);
            }
            
            // Verify the PDF was saved
            const verifyResponse = await fetch(`${API_URL}/api/pdf/debug/${formId}`);
            if (verifyResponse.ok) {
              const verifyData = await verifyResponse.json();
              const page2Saved = verifyData.pages?.some(p => p.page_number === 2);
              if (page2Saved) {
                console.log('[Page2] PDF saved and verified successfully');
                saveSuccessful = true;
                break;
              } else {
                throw new Error('PDF not found after save');
              }
            }
          } catch (err) {
            console.error(`[Page2] Save attempt ${attempt} failed:`, err.message);
            if (attempt < MAX_RETRIES) {
              await new Promise(r => setTimeout(r, 2000));
            }
          }
        }
        
        if (!saveSuccessful) {
          const overlayEl = document.getElementById('pdf-generation-overlay');
          if (overlayEl) overlayEl.remove();
          setIsGeneratingPDF(false);
          alert('Failed to save your progress after multiple attempts. Please check your internet connection and try again.');
          return;
        }
      }
      
      // Remove overlay
      const overlayEl = document.getElementById('pdf-generation-overlay');
      if (overlayEl) overlayEl.remove();
      
      setIsGeneratingPDF(false);
      
      // Navigate to Page 3 with all client data
      navigate('/page-3', { 
        state: { 
          ...clientData,
          signedAtPage2: signedAt,
          signaturePage2: signature
        } 
      });
      
    } catch (error) {
      console.error('[Page2] PDF generation error:', error);
      const overlayEl = document.getElementById('pdf-generation-overlay');
      if (overlayEl) overlayEl.remove();
      setIsGeneratingPDF(false);
      alert('An error occurred while generating your PDF. Please try again.');
    }
  };

  const handleBack = () => {
    // Pass all the client data back to Page 1 so fields remain populated
    navigate('/page-1', { 
      state: { 
        ...clientData,
        // Also pass back Page 2 data in case they return
        signedAtPage2: signedAt,
        signaturePage2: signature
      } 
    });
  };

  return (
    <div className="min-h-screen bg-background flex flex-col">
      {/* Main Content - wrapped for PDF capture */}
      <main id="page-pdf-content" className="flex-1 flex flex-col items-center px-4 pt-[2px] pb-6 sm:pb-8 bg-white">
        {/* Logo Section */}
        <div className="mb-4 sm:mb-6 flex flex-col items-center gap-3">
          <img 
            src={LOGO_URL} 
            alt="TCA Auditors Logo" 
            className="h-16 sm:h-20 w-auto object-contain"
          />
          <img 
            src={BANNER_URL} 
            alt="Tax & Auditing Consultants" 
            className="h-10 sm:h-14 w-auto object-contain"
          />
        </div>

        {/* Main Heading */}
        <div className="mb-6 pb-2">
          <h1 className="text-xl sm:text-2xl font-extrabold text-tca-blue text-center tracking-wide inline-block underline-tca-blue">
            TAX SERVICES AGREEMENT
          </h1>
        </div>

        {/* Power of Attorney Document */}
        <Card className="w-full max-w-4xl border shadow-card">
          <CardContent className="p-4 sm:p-8">
            {/* SARS Header */}
            <div className="flex justify-between items-start mb-6 border-b pb-4">
              <div className="flex items-center gap-2">
                <img 
                  src="/images/SARS_Logo.png" 
                  alt="SARS Logo" 
                  className="h-[6.5rem] sm:h-[8.5rem] w-auto object-contain"
                />
              </div>
              <div className="text-right">
                <p className="text-xl sm:text-2xl font-bold text-gray-900 underline">SOUTH AFRICAN REVENUE SERVICE</p>
                <p className="text-lg font-semibold text-gray-700">Special Power of Attorney to Tax Practitioner</p>
              </div>
            </div>

            {/* Document Content */}
            <div className="space-y-4 text-sm sm:text-base text-gray-800 leading-relaxed">
              <p className="text-gray-600 font-bold">The Commissioner for the South African Revenue Service</p>

              {/* Client Name Section */}
              <p>
                I, the undersigned, <span className="font-bold text-tca-blue border-b-2 border-tca-blue px-2 py-1 bg-blue-50">{fullName}</span>{' '}
                Identity/Pass. Number: <span className="font-bold text-tca-blue border-b-2 border-tca-blue px-2 py-1 bg-blue-50">{idNumber}</span>
              </p>

              <p>
                in my personal capacity hereby appoint <strong>Schalk P.B. Coetzee</strong>, Identity/Passport Number: <strong>8811205075080</strong> of Tax
                Practitioner Firm <strong>TC Auditors</strong>, to act on my behalf in respect of the following tax matter(s) detailed below:
              </p>

              {/* Tax Matters */}
              <div className="bg-gray-50 p-4 rounded-lg">
                <p className="font-semibold mb-2">1. INCOME TAX REFERENCE: <span className="font-bold text-tca-blue bg-blue-50 px-2 py-1 rounded">{taxNumber}</span></p>
                <ul className="list-disc list-inside space-y-1 ml-4 text-gray-700">
                  <li>To communicate to SARS any change of registered particulars</li>
                  <li>To request and/or follow up on the issuing of tax clearance certificates</li>
                  <li>To complete and/or submit returns to SARS</li>
                  <li>To communicate with SARS and to submit relevant material to SARS</li>
                  <li>To lodge and pursue an objection against an assessment raised or decision made by SARS</li>
                  <li>To file and pursue an appeal against an assessment raised or decision made by SARS</li>
                </ul>
              </div>

              <p>
                I confirm that the authority that has been delegated to <strong>Schalk P.B. Coetzee</strong> by this Special Power of Attorney may
                be performed by a person who is under his or her direct supervision.
              </p>

              <p>
                I confirm, for the purpose of absolute clarity that anything done by <strong>Schalk P.B. Coetzee</strong> or person under his/her
                direct supervision pursuant to this Special Power of Attorney shall be regarded, for all intents and purposes, as
                having been done by myself and I undertake to ratify any actions taken in terms of this Special Power of Attorney.
              </p>

              <p>
                This Special Power of Attorney shall operate for a period of <strong>24 month(s)</strong> from the date of my signature.
              </p>

              {/* Date Section */}
              <div className="bg-gray-50 p-4 rounded-lg mt-6">
                <p>
                  Signed at <input 
                    type="text" 
                    value={signedAt}
                    onChange={handleLocationChange}
                    onBlur={(e) => setSignedAt(e.target.value.trim())}
                    placeholder="Enter location"
                    className="border-b-2 border-tca-blue bg-blue-50 px-2 py-1 w-40 focus:outline-none font-bold text-tca-blue rounded"
                  /> on this <span className="font-bold text-tca-blue bg-blue-50 px-2 py-1 rounded">{dayOfMonth}</span> day of <span className="font-bold text-tca-blue bg-blue-50 px-2 py-1 rounded">{monthYear}</span>
                </p>
                {locationError && <p className="text-sm text-red-600 mt-2">{locationError}</p>}
              </div>

              {/* Signature Section */}
              <div className="mt-6">
                <p className="font-semibold mb-3">Signature:</p>
                <SignatureCanvas 
                  ref={signatureCanvasRef}
                  onSignatureChange={handleSignatureChange}
                  onClear={handleClearSignature}
                  initialSignature={signature}
                  heightClass="h-40 sm:h-48"
                />
              </div>

              <br />
              <br />
              <br />

              {/* Witness Section */}
              <div className="mt-6">
                <p className="font-semibold">As witnesses:</p>
                <br />
                <br />
                
                {/* Witness 1 */}
                <div className="flex items-center gap-2">
                  <span className="font-bold">1.</span>
                  <div className="w-52 border-b border-gray-400" data-witness-line="1"></div>
                  <span className="text-sm font-bold">Full Name:</span>
                  <span className="font-bold text-tca-blue bg-blue-50 px-2 py-1 rounded">Ruan England</span>
                </div>
                
                <br />
                <br />
                <br />
                
                {/* Witness 2 */}
                <div className="flex items-center gap-2">
                  <span className="font-bold">2.</span>
                  <div className="w-52 border-b border-gray-400" data-witness-line="2"></div>
                  <span className="text-sm font-bold">Full Name:</span>
                  <span className="font-bold text-tca-blue bg-blue-50 px-2 py-1 rounded">Anel Barwise</span>
                </div>
                
                <br />
                <br />
                <br />
                <br />
              </div>

              {/* Footer */}
              <div className="flex justify-between items-start mt-8 pt-4 border-t text-xs text-gray-500">
                <span>Version: 2016.01.00</span>
                <div className="text-right">
                  <div>Form ID: TPPOA</div>
                  <div>Page of Page: 01/01</div>
                </div>
              </div>
            </div>

            {/* Buttons - Hidden in PDF */}
            <div className="flex flex-col sm:flex-row gap-3 items-center justify-center pt-6" data-hide-in-pdf="true">
              <a 
                href="https://tax.tcauditors.co.za"
                className="inline-flex items-center justify-center text-xs font-bold px-4 py-2 h-9 rounded-md bg-slate-600 hover:bg-slate-700 text-white transition-all duration-200 shadow-sm"
                data-hide-in-pdf="true"
              >
                Start Over
              </a>
              <Button 
                type="button"
                variant="outline"
                onClick={handleBack}
                className="sm:w-auto"
              >
                ← Back to Page 1
              </Button>
              
              <Button 
                type="button"
                onClick={handleSubmit}
                disabled={!isFormValid() || isGeneratingPDF}
                className="sm:w-auto bg-accent hover:bg-accent/90 text-accent-foreground font-semibold py-3 px-8 h-12 text-base rounded-lg shadow-soft hover:shadow-card transition-all duration-200 disabled:opacity-50 disabled:cursor-not-allowed"
              >
                {isGeneratingPDF ? 'Saving...' : 'Go to Next Page...'}
              </Button>
            </div>
          </CardContent>
        </Card>
      </main>

      {/* Page Indicator with TCA Logo */}
      <div className="relative">
        <PageIndicator currentPage={2} totalPages={7} pageTitle="SARS Power of Attorney" />
        <img 
          src={TCA_SHORT_LOGO} 
          alt="TCA Logo" 
          className="absolute bottom-2 right-56 h-12 w-auto object-contain opacity-80"
        />
      </div>
    </div>
  );
};

export default PageTwo;
